import React, {useState, useEffect} from 'react';
import {
  View,
  Text,
  Image,
  StyleSheet,
  ScrollView,
  TextInput,
} from 'react-native';
import {useTranslation} from 'react-i18next';
import {MATERIAL_ICONS_FONT} from '../styles/fonts';
import {donationService} from '../services';
import {useAppNavigation} from '../hooks/useAppNavigation';
import {useAppDispatch} from '../store';
import {logout} from '../store/authSlice';
import {
  SettingsLayout,
  GlassPanel,
  Heading,
  BodyText,
  MutedText,
  Button,
  theme,
} from '../components/DarkTheme';
import OfflineBanner from '../components/OfflineBanner';
import {NetworkError} from '../services/api';

function getQueryParam(name: string): string | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  return params.get(name);
}

export default function DonationScreen() {
  const {t} = useTranslation();
  const navigation = useAppNavigation();
  const dispatch = useAppDispatch();

  const [amount, setAmount] = useState('5.00');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<'success' | 'cancelled' | null>(null);

  useEffect(() => {
    const s = getQueryParam('status');
    if (s === 'success' || s === 'cancelled') {
      setStatus(s);
    }
  }, []);

  const handleDonate = async () => {
    setError(null);
    const cents = Math.round(parseFloat(amount) * 100);
    if (isNaN(cents) || cents < 100) {
      setError(t('donation.minimumAmount'));
      return;
    }

    setIsLoading(true);
    try {
      const {url} = await donationService.createCheckoutSession(cents);
      window.location.href = url;
    } catch (e) {
      setError(e instanceof NetworkError ? t('donation.offlineError') : t('errors.unknownError'));
      setIsLoading(false);
    }
  };

  const handleLogout = async () => {
    await dispatch(logout());
  };

  return (
    <SettingsLayout
      currentScreen="donation"
      onNavigate={(screen, params) => navigation.navigate(screen, params)}
      onLogout={handleLogout}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}>
        <View style={styles.content}>
          <OfflineBanner />

          {/* Status messages */}
          {status === 'success' && (
            <GlassPanel centered style={styles.statusPanelSuccess}>
              <Text style={styles.thankYouEmoji}>🎉</Text>
              <BodyText style={styles.successText}>
                {t('donation.success')}
              </BodyText>
            </GlassPanel>
          )}
          {status === 'cancelled' && (
            <GlassPanel style={styles.statusPanel}>
              <Text style={styles.cancelledEmoji}>🍪</Text>
              <BodyText style={styles.cancelledText}>
                {t('donation.cancelled')}
              </BodyText>
            </GlassPanel>
          )}

          {/* Main donation card */}
          <GlassPanel centered style={styles.donationCard}>
            <View style={styles.sunburstBg}>
              <View style={styles.sunburst} />
            </View>
            <Image
              source={{uri: '/oma-cookie.png'}}
              style={styles.omaImage}
              resizeMode="contain"
            />

            <Heading style={styles.title}>{t('donation.title')}</Heading>
            <BodyText style={styles.subtitle}>{t('donation.subtitle')}</BodyText>

            <View style={styles.amountSection}>
              <MutedText style={styles.amountLabel}>{t('donation.amount')}</MutedText>
              <View style={styles.amountInputRow}>
                <Text style={styles.currencySymbol}>€</Text>
                <TextInput
                  style={styles.amountInput}
                  value={amount}
                  onChangeText={setAmount}
                  keyboardType="decimal-pad"
                  placeholder="5.00"
                  placeholderTextColor={theme.textSubtle}
                  selectTextOnFocus
                />
              </View>
              {error && <Text style={styles.errorText}>{error}</Text>}
            </View>

            <View style={styles.presetRow}>
              {['2', '5', '10', '20'].map(val => (
                <Button
                  key={val}
                  label={`€${val}`}
                  variant={amount === `${val}.00` || amount === val ? 'primary' : 'secondary'}
                  onPress={() => setAmount(`${val}.00`)}
                />
              ))}
            </View>

            <View style={styles.donateButtonContainer}>
              <Button
                label={isLoading ? t('common.redirecting') : t('donation.donate')}
                icon="volunteer_activism"
                onPress={handleDonate}
                disabled={isLoading}
              />
            </View>
          </GlassPanel>
        </View>
      </ScrollView>
    </SettingsLayout>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 32,
    alignItems: 'center',
    minHeight: '100%',
  },
  content: {
    maxWidth: 520,
    width: '100%',
    gap: 24,
  },

  // Status panels
  statusPanelSuccess: {
    padding: 24,
    gap: 12,
  },
  statusPanel: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
    padding: 20,
  },
  thankYouEmoji: {
    fontSize: 48,
  },
  cancelledEmoji: {
    fontSize: 32,
  },
  successText: {
    color: theme.success,
    textAlign: 'center',
    lineHeight: 24,
  },
  cancelledText: {
    flex: 1,
    color: theme.textMuted,
  },

  // Donation card
  donationCard: {
    width: '100%',
    overflow: 'hidden',
    position: 'relative',
  },
  sunburstBg: {
    position: 'absolute',
    top: -300,
    left: -300,
    right: -300,
    bottom: -300,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sunburst: {
    width: 1200,
    height: 1200,
    marginTop: -500,
    backgroundImage:
      'repeating-conic-gradient(from 0deg, #FF6B3D 0deg 15deg, #FFBA42 15deg 30deg)',
    opacity: 0.35,
  } as any,
  omaImage: {
    width: 190,
    height: 190,
    marginBottom: 16,
    zIndex: 1,
  },
  title: {
    marginBottom: 12,
    textAlign: 'center',
  },
  subtitle: {
    textAlign: 'center',
    color: theme.textMuted,
    maxWidth: 400,
    lineHeight: 24,
    marginBottom: 32,
  },

  // Amount section
  amountSection: {
    width: '100%',
    marginBottom: 20,
  },
  amountLabel: {
    marginBottom: 8,
    fontSize: 14,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  amountInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.surface,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: theme.border,
    paddingHorizontal: 16,
  },
  currencySymbol: {
    fontSize: 24,
    fontWeight: '600',
    color: theme.text,
    marginRight: 8,
  },
  amountInput: {
    flex: 1,
    fontSize: 24,
    fontWeight: '600',
    color: theme.text,
    paddingVertical: 14,
    outlineStyle: 'none',
  } as any,
  errorText: {
    color: theme.error,
    fontSize: 14,
    marginTop: 8,
  },

  // Preset buttons
  presetRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },

  // Donate button
  donateButtonContainer: {
    width: '100%',
  },
});
